import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { runnerApi, downloadBlob, errorText, type Batch, type HistoryRun, type MarkRow } from '../../../api/migrationRunner';
import { BATCH_TONE, Btn, ErrorBox, Panel, Pill, RUN_LABEL, RUN_TONE, Spinner, duration, shortName, when } from './ui';
import type { RunnerNav } from './nav';

type Range = '1' | '7' | '30' | '90' | 'all';

const MARK_KIND: Record<string, string> = { mark: 'Marked', bulk: 'Marked in bulk', baseline: 'Start fresh baseline', stop: 'Marked stopped', clear_stuck: 'Cleared all stuck', clear_pending: 'Cleared pending up to a date' };

/** Batches and every run, filterable, with CSV export (approved mockup, "History"). */
const HistoryTab: React.FC<{ nav: RunnerNav; refreshKey: number }> = ({ nav, refreshKey }) => {
  const [view, setView] = useState<'batches' | 'runs' | 'marks'>('batches');
  const [marks, setMarks] = useState<MarkRow[] | null>(null);
  const [undoing, setUndoing] = useState<string | null>(null);
  const [range, setRange] = useState<Range>('30');
  const [who, setWho] = useState('');
  const [status, setStatus] = useState('');
  const [target, setTarget] = useState('');
  const [migration, setMigration] = useState('');
  const [batches, setBatches] = useState<Batch[] | null>(null);
  const [runs, setRuns] = useState<HistoryRun[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const params = useMemo(() => {
    const p: Record<string, string> = {};
    if (range !== 'all') p.from = new Date(Date.now() - Number(range) * 86400000).toISOString();
    if (who.trim()) p[view === 'batches' ? 'createdBy' : 'executedBy'] = who.trim();
    if (status) p.status = status;
    if (view === 'runs' && target.trim()) p.target = target.trim();
    if (view === 'runs' && migration.trim()) p.migration = migration.trim();
    return p;
  }, [range, who, status, target, migration, view]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (view === 'batches') setBatches(await runnerApi.batches({ ...params, limit: 200 }));
      else if (view === 'marks') setMarks(await runnerApi.marks({ limit: 200 }));
      else setRuns(await runnerApi.history({ ...params, limit: 500 }));
    } catch (err) {
      const msg = errorText(err, 'Could not load history');
      setError(msg);
      if (view === 'batches') setBatches([]);
      if (view === 'marks') setMarks([]);
    } finally {
      setLoading(false);
    }
  }, [view, params]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const exportCsv = async () => {
    try {
      downloadBlob(await runnerApi.historyCsv({ ...params }), `migration-history-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (err) {
      setError(errorText(err, 'Export failed'));
    }
  };

  const statuses = view === 'batches'
    ? ['', 'running', 'paused', 'done', 'failed', 'cancelled', 'interrupted']
    : ['', 'failed,partial,interrupted', 'success', 'failed', 'partial', 'interrupted', 'resolved', 'running'];

  return (
    <div className="space-y-4 px-6 py-6">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-slate-300 bg-white p-0.5">
          {(['batches', 'runs', 'marks'] as const).map((v) => (
            <button key={v} type="button" onClick={() => { setView(v); setStatus(''); }}
              className={`rounded-md px-3 py-1.5 text-[13px] font-bold ${view === v ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
              {v === 'batches' ? 'Batches' : v === 'runs' ? 'Every run' : 'Clean-up marks'}
            </button>
          ))}
        </div>
        <select value={range} onChange={(e) => setRange(e.target.value as Range)} className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-[13px]">
          <option value="1">Last 24 hours</option>
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="all">All time</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-[13px]">
          {statuses.map((s) => (
            <option key={s} value={s}>{s === '' ? 'Any result' : s === 'failed,partial,interrupted' ? 'Problems only' : (RUN_LABEL[s] ?? s)}</option>
          ))}
        </select>
        <input value={who} onChange={(e) => setWho(e.target.value)} placeholder="Operator email" className="w-48 rounded-lg border border-slate-300 px-2.5 py-1.5 text-[13px]" />
        {view === 'runs' && (
          <>
            <input value={migration} onChange={(e) => setMigration(e.target.value)} placeholder="Migration" className="w-40 rounded-lg border border-slate-300 px-2.5 py-1.5 text-[13px]" />
            <input value={target} onChange={(e) => setTarget(e.target.value)} placeholder="Database" className="w-36 rounded-lg border border-slate-300 px-2.5 py-1.5 text-[13px]" />
          </>
        )}
        <span className="flex-1" />
        {loading && <Spinner />}
        {view === 'runs' && <Btn kind="ghost" onClick={exportCsv}>Export CSV</Btn>}
      </div>

      {error && <ErrorBox>{error}</ErrorBox>}

      {view === 'marks' ? (
        <Panel bodyClass="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2">When</th><th className="px-3 py-2">Who</th><th className="px-3 py-2">What</th><th className="px-3 py-2">Note</th><th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 align-top">
              {(marks ?? []).map((m) => (
                <tr key={m.id}>
                  <td className="whitespace-nowrap px-3 py-2">{when(m.createdAt)}</td>
                  <td className="px-3 py-2">{m.createdBy}</td>
                  <td className="px-3 py-2">
                    {MARK_KIND[m.kind] ?? m.kind}{m.kind !== 'stop' ? ` · ${m.count.toLocaleString()} pair${m.count === 1 ? '' : 's'}` : ''}
                    {m.summary?.archived?.length ? ` · ${m.summary.archived.length} files archived` : ''}
                    {m.hidden && <span className="ml-1.5"><Pill tone="mut">hidden from runs</Pill></span>}
                  </td>
                  <td className="px-3 py-2 text-slate-500">{m.note}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right">
                    {m.undoneAt
                      ? <Pill tone="mut">undone {when(m.undoneAt)}</Pill>
                      : m.canUndo && (
                        <Btn kind="ghost" small disabled={undoing === m.id} onClick={async () => {
                          setUndoing(m.id);
                          try { await runnerApi.undoMark(m.id); await load(); } catch (err) { setError(errorText(err, 'Undo failed')); } finally { setUndoing(null); }
                        }}>
                          {undoing === m.id ? 'Undoing…' : 'Undo'}
                        </Btn>
                      )}
                  </td>
                </tr>
              ))}
              {marks && marks.length === 0 && !error && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">No marks yet. Marks made on Clean up (and Resolve) show here, each undoable for 30 days.</td></tr>
              )}
            </tbody>
          </table>
        </Panel>
      ) : view === 'batches' ? (
        <Panel bodyClass="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2">When</th><th className="px-3 py-2">Who</th><th className="px-3 py-2">What</th><th className="px-3 py-2">Steps</th><th className="px-3 py-2">Result</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(batches ?? []).map((b) => (
                <tr key={b.id} className="cursor-pointer hover:bg-slate-50" onClick={() => nav.openBatch(b.id)}>
                  <td className="whitespace-nowrap px-3 py-2">{when(b.createdAt)}</td>
                  <td className="px-3 py-2">{b.createdBy}</td>
                  <td className="px-3 py-2">
                    {b.options.label ? `${b.options.label} · ` : ''}
                    {b.summary.migrations} migration{b.summary.migrations === 1 ? '' : 's'} × {b.summary.companies} database{b.summary.companies === 1 ? '' : 's'}
                  </td>
                  <td className="px-3 py-2 text-slate-500">
                    {Object.entries(b.summary.counts).map(([k, v]) => `${v} ${k.replace('_', ' ')}`).join(' · ')}
                  </td>
                  <td className="px-3 py-2"><Pill tone={BATCH_TONE[b.status] ?? 'mut'}>{b.status}</Pill></td>
                </tr>
              ))}
              {batches && batches.length === 0 && !error && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">No batches in this range.</td></tr>
              )}
            </tbody>
          </table>
        </Panel>
      ) : (
        <Panel bodyClass="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2">When</th><th className="px-3 py-2">Who</th><th className="px-3 py-2">Migration</th><th className="px-3 py-2">Database</th><th className="px-3 py-2">Result</th><th className="px-3 py-2">Took</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(runs ?? []).map((r) => (
                <tr key={r.id} className="cursor-pointer align-top hover:bg-slate-50" onClick={() => nav.openRun(r.id)}>
                  <td className="whitespace-nowrap px-3 py-2">{when(r.executed_at)}</td>
                  <td className="px-3 py-2">{r.executed_by}</td>
                  <td className="px-3 py-2">{r.folder_path || r.migration_name ? shortName(r.folder_path || r.migration_name) : <span className="text-slate-400">ad-hoc SQL</span>}</td>
                  <td className="px-3 py-2">{r.company_name || (r.target_db === '__registry__' ? 'Registry' : r.target_db)}</td>
                  <td className="px-3 py-2">
                    <Pill tone={RUN_TONE[r.status] ?? 'mut'}>{RUN_LABEL[r.status] ?? r.status}</Pill>
                    {r.failed_statement_index !== null && r.failed_statement_index !== undefined && (
                      <span className="ml-1.5 text-xs text-slate-500">at statement {r.failed_statement_index + 1}</span>
                    )}
                    {r.status === 'resolved' && r.resolved_note && <div className="mt-0.5 text-xs text-slate-500">"{r.resolved_note}"</div>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-slate-500">{duration(r.duration_ms)}</td>
                </tr>
              ))}
              {runs && runs.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500">No runs match.</td></tr>
              )}
            </tbody>
          </table>
        </Panel>
      )}
    </div>
  );
};

export default HistoryTab;
